import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../../contexts/AuthContext';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Material {
  id: string;
  title: string;
  description: string;
  category: string;
  grade_level: string;
  price: number;
  preview_file_url: string;
  full_file_url: string;
  thumbnail_url: string;
  page_count: number;
  file_size: number;
}

interface Review {
  id: string;
  user_name: string;
  rating: number;
  comment: string;
  created_at: string;
  replies?: Reply[];
}

interface Reply {
  id: string;
  admin_name: string;
  reply_content: string;
  created_at: string;
}

interface ReviewStats {
  reviews: Review[];
  total: number;
  averageRating: number;
  ratingCounts: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
}

export default function MaterialDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [material, setMaterial] = useState<Material | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPurchased, setIsPurchased] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [reviewStats, setReviewStats] = useState<ReviewStats | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyingToReviewId, setReplyingToReviewId] = useState<string | null>(null);
  const [adminName, setAdminName] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [couponMessage, setCouponMessage] = useState('');

  useEffect(() => {
    fetchMaterial();
  }, [id]);

  useEffect(() => {
    if (user) {
      setUserEmail(user.email || '');
      setUserName(user.user_metadata?.full_name || '');
      if (user.email) {
        checkPurchase(user.email);
      }
    }
  }, [user]);

  useEffect(() => {
    if (id) {
      fetchReviews();
    }
  }, [id]);

  const fetchMaterial = async () => {
    try {
      const { data, error } = await supabase
        .from('materials')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setMaterial(data);
    } catch (error) {
      console.error('獲取教材失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPurchase = async (email: string) => {
    try {
      const response = await fetch(
        `https://fnhsumfewqfgpjteloga.supabase.co/functions/v1/check-purchase?user_email=${email}&material_id=${id}`
      );
      const data = await response.json();
      setIsPurchased(data.purchased);
    } catch (error) {
      console.error('檢查購買記錄失敗:', error);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      // 如果未登入，跳轉到登入頁面
      window.location.href = '/login';
      return;
    }

    if (!userEmail || !userName) {
      alert('請填寫完整資料');
      return;
    }

    setPurchasing(true);
    try {
      const currentUrl = window.location.origin;
      const response = await fetch(
        'https://fnhsumfewqfgpjteloga.supabase.co/functions/v1/create-stripe-checkout',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            material_id: id,
            user_email: userEmail,
            user_name: userName,
            success_url: `${currentUrl}/material-detail/${id}?success=true&email=${encodeURIComponent(userEmail)}`,
            cancel_url: `${currentUrl}/material-detail/${id}?canceled=true`,
          }),
        }
      );

      const data = await response.json();
      if (data.url) {
        // 跳轉到 Stripe 支付頁面
        window.location.href = data.url;
      } else {
        throw new Error('無法創建支付連結');
      }
    } catch (error) {
      console.error('購買失敗:', error);
      alert('購買失敗，請稍後再試');
      setPurchasing(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await fetch(
        `https://fnhsumfewqfgpjteloga.supabase.co/functions/v1/get-reviews?material_id=${id}`
      );
      const data = await response.json();
      
      // 獲取每個評價的回覆
      if (data.reviews) {
        const reviewsWithReplies = await Promise.all(
          data.reviews.map(async (review: Review) => {
            try {
              const { data: replies } = await supabase
                .from('review_replies')
                .select('*')
                .eq('review_id', review.id)
                .order('created_at', { ascending: true });
              
              return {
                ...review,
                replies: replies || [],
              };
            } catch {
              return review;
            }
          })
        );
        
        setReviewStats({
          ...data,
          reviews: reviewsWithReplies,
        });
      } else {
        setReviewStats(data);
      }
    } catch (error) {
      console.error('獲取評價失敗:', error);
    }
  };

  const handleSubmitReview = async () => {
    if (!user || !isPurchased) {
      alert('您需要先購買此教材才能評價');
      return;
    }

    setSubmittingReview(true);
    try {
      const response = await fetch(
        'https://fnhsumfewqfgpjteloga.supabase.co/functions/v1/submit-review',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            material_id: id,
            user_email: userEmail,
            user_name: userName,
            rating: reviewRating,
            comment: reviewComment,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        // 創建評價獎勵優惠券
        try {
          const couponResponse = await fetch(
            'https://fnhsumfewqfgpjteloga.supabase.co/functions/v1/create-review-coupon',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_email: userEmail,
              }),
            }
          );
          
          const couponData = await couponResponse.json();
          if (couponData.success) {
            setCouponMessage(`🎉 ${couponData.message}\n優惠券代碼：${couponData.coupon.coupon_code}`);
          }
        } catch (error) {
          console.error('創建優惠券失敗:', error);
        }
        
        alert(data.message + (couponMessage ? '\n\n' + couponMessage : ''));
        setShowReviewModal(false);
        setReviewComment('');
        setReviewRating(5);
        fetchReviews();
      } else {
        alert(data.error || '提交失敗');
      }
    } catch (error) {
      console.error('提交評價失敗:', error);
      alert('提交失敗，請稍後再試');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!replyingToReviewId || !adminName || !adminPassword || !replyContent) {
      alert('請填寫完整資料');
      return;
    }

    setSubmittingReply(true);
    try {
      const response = await fetch(
        'https://fnhsumfewqfgpjteloga.supabase.co/functions/v1/submit-reply',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            review_id: replyingToReviewId,
            admin_name: adminName,
            admin_password: adminPassword,
            reply_content: replyContent,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        alert('回覆已提交');
        setShowReplyModal(false);
        setReplyingToReviewId(null);
        setAdminName('');
        setAdminPassword('');
        setReplyContent('');
        fetchReviews();
      } else {
        alert(data.error || '回覆失敗');
      }
    } catch (error) {
      console.error('提交回覆失敗:', error);
      alert('回覆失敗，請稍後再試');
    } finally {
      setSubmittingReply(false);
    }
  };

  // 檢查支付結果
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const email = urlParams.get('email');
    
    if (success === 'true' && email) {
      setUserEmail(email);
      checkPurchase(email);
      // 清除 URL 參數
      window.history.replaceState({}, '', `/material-detail/${id}`);
      alert('支付成功！您現在可以下載完整教材了');
    }
    
    if (urlParams.get('canceled') === 'true') {
      // 清除 URL 參數
      window.history.replaceState({}, '', `/material-detail/${id}`);
      alert('支付已取消');
    }
  }, [id]);

  const handleDownload = async () => {
    if (!material?.full_file_url) return;

    try {
      // 更新下載次數
      await supabase
        .from('user_purchases')
        .update({
          download_count: supabase.rpc('increment', { x: 1 }),
          last_download_at: new Date().toISOString(),
        })
        .eq('user_email', userEmail)
        .eq('material_id', id);

      // 開始下載
      window.open(material.full_file_url, '_blank');
    } catch (error) {
      console.error('下載失敗:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!material) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">找不到教材</h2>
          <Link to="/materials" className="text-orange-500 hover:text-orange-600">
            返回教材列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img src="https://public.readdy.ai/ai/img_res/46c4b423-33ec-4c59-b54c-70c60984f453.png" alt="Logo" className="h-10 w-10 object-contain" />
              <span className="text-xl font-bold text-gray-900">數學學習平台</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/materials" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                ← 返回教材列表
              </Link>
              {user && (
                <Link to="/profile" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                  會員中心
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left: Image */}
            <div className="space-y-6">
              <div className="rounded-3xl overflow-hidden bg-gray-100 aspect-[3/4] w-full h-auto">
                <img
                  src={material.thumbnail_url || `https://public.readdy.ai/ai/img_res/40a3a2103e9f80f4d8b1784bcc224d9e.jpg$%7Bmaterial.category%7D%20$%7Bmaterial.grade_level%7D%20level%2C%20professional%20educational%20design%2C%20clean%20modern%20layout%2C%20soft%20background&width=600&height=800&seq=${material.id}&orientation=portrait`}
                  alt={material.title}
                  className="w-full h-full object-cover"
                />
              </div>
              {material.preview_file_url && (
                <a
                  href={material.preview_file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-3 bg-gray-100 text-gray-900 rounded-full text-center font-semibold hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  <i className="ri-eye-line mr-2"></i>
                  預覽教材樣本
                </a>
              )}
            </div>

            {/* Right: Details */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {material.grade_level}
                  </span>
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                    {material.category}
                  </span>
                  {reviewStats && reviewStats.total > 0 && (
                    <div className="flex items-center space-x-1 px-3 py-1 bg-yellow-50 rounded-full">
                      <i className="ri-star-fill text-yellow-500 text-sm"></i>
                      <span className="text-sm font-semibold text-gray-900">
                        {reviewStats.averageRating}
                      </span>
                      <span className="text-xs text-gray-600">
                        ({reviewStats.total})
                      </span>
                    </div>
                  )}
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  {material.title}
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {material.description}
                </p>
              </div>

              <div className="border-t border-b border-gray-200 py-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">頁數</span>
                  <span className="font-semibold text-gray-900">{material.page_count} 頁</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">檔案大小</span>
                  <span className="font-semibold text-gray-900">
                    {(material.file_size / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">格式</span>
                  <span className="font-semibold text-gray-900">PDF</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-6 space-y-4">
                <div className="flex items-baseline space-x-2">
                  <span className="text-5xl font-bold text-gray-900">HK$ {material.price}</span>
                </div>
                <p className="text-sm text-gray-600">
                  一次購買，永久使用。包含完整教材內容與練習題。
                </p>
              </div>

              {!isPurchased ? (
                <div className="space-y-3">
                  <button
                    onClick={() => user ? setShowPurchaseModal(true) : (window.location.href = '/login')}
                    className="w-full py-4 bg-blue-900 text-white rounded-full text-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
                  >
                    {user ? '立即購買' : '登入後購買'}
                  </button>
                  <p className="text-center text-sm text-gray-600">
                    {user ? '購買後可立即下載完整教材' : '請先登入以購買教材'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <button
                    onClick={handleDownload}
                    className="w-full py-4 bg-green-600 text-white rounded-full text-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
                  >
                    <i className="ri-download-line mr-2"></i>
                    下載完整教材
                  </button>
                  <button
                    onClick={() => setShowReviewModal(true)}
                    className="w-full py-4 bg-orange-500 text-white rounded-full text-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
                  >
                    <i className="ri-star-line mr-2"></i>
                    評價此教材
                  </button>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center space-x-2 text-green-700">
                      <i className="ri-checkbox-circle-fill text-xl"></i>
                      <span className="font-medium">您已購買此教材</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 space-y-3">
                <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                  <i className="ri-information-line text-blue-600"></i>
                  <span>購買須知</span>
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-blue-600 mt-0.5"></i>
                    <span>購買後可無限次下載與列印</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-blue-600 mt-0.5"></i>
                    <span>如有疑問可預約線上教學指導</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-blue-600 mt-0.5"></i>
                    <span>支援AI作業批改功能</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Reviews Section */}
          {reviewStats && reviewStats.total > 0 && (
            <div className="mt-16">
              <div className="bg-white rounded-3xl p-8 border border-gray-200">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">教材評價</h2>

                {/* Rating Summary */}
                <div className="grid md:grid-cols-2 gap-8 mb-12 pb-8 border-b border-gray-200">
                  <div className="text-center">
                    <div className="text-6xl font-bold text-gray-900 mb-2">
                      {reviewStats.averageRating}
                    </div>
                    <div className="flex items-center justify-center space-x-1 mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`text-2xl ${
                            star <= Math.round(reviewStats.averageRating)
                              ? 'ri-star-fill text-yellow-500'
                              : 'ri-star-line text-gray-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-gray-600">基於 {reviewStats.total} 則評價</p>
                  </div>

                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((star) => (
                      <div key={star} className="flex items-center space-x-3">
                        <span className="text-sm text-gray-600 w-12">{star} 星</span>
                        <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-yellow-500 rounded-full"
                            style={{
                              width: `${
                                reviewStats.total > 0
                                  ? (reviewStats.ratingCounts[star as keyof typeof reviewStats.ratingCounts] /
                                      reviewStats.total) *
                                    100
                                  : 0
                              }%`,
                            }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600 w-12 text-right">
                          {reviewStats.ratingCounts[star as keyof typeof reviewStats.ratingCounts]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reviews List */}
                <div className="space-y-6">
                  {reviewStats.reviews.map((review) => (
                    <div key={review.id} className="border-b border-gray-200 pb-6 last:border-0">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-900 to-orange-500 rounded-full text-white font-semibold">
                            {review.user_name[0]}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{review.user_name}</div>
                            <div className="flex items-center space-x-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <i
                                  key={star}
                                  className={`text-sm ${
                                    star <= review.rating
                                      ? 'ri-star-fill text-yellow-500'
                                      : 'ri-star-line text-gray-300'
                                  }`}
                                ></i>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-500">
                            {new Date(review.created_at).toLocaleDateString('zh-TW')}
                          </span>
                          <button
                            onClick={() => {
                              setReplyingToReviewId(review.id);
                              setShowReplyModal(true);
                            }}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-200 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-reply-line mr-1"></i>
                            回覆
                          </button>
                        </div>
                      </div>
                      {review.comment && (
                        <p className="text-gray-700 leading-relaxed ml-15 mb-4">{review.comment}</p>
                      )}
                      
                      {/* Admin Replies */}
                      {review.replies && review.replies.length > 0 && (
                        <div className="ml-15 space-y-3 mt-4">
                          {review.replies.map((reply) => (
                            <div key={reply.id} className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full text-white text-xs font-semibold">
                                    <i className="ri-admin-line"></i>
                                  </div>
                                  <div>
                                    <div className="font-semibold text-blue-900 text-sm">{reply.admin_name}</div>
                                    <div className="text-xs text-gray-500">
                                      {new Date(reply.created_at).toLocaleDateString('zh-TW')}
                                    </div>
                                  </div>
                                </div>
                                <span className="px-2 py-1 bg-blue-600 text-white rounded-full text-xs font-medium">
                                  官方回覆
                                </span>
                              </div>
                              <p className="text-gray-700 text-sm leading-relaxed">{reply.reply_content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Purchase Modal */}
      {showPurchaseModal && user && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">完成購買</h2>
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  姓名
                </label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="請輸入您的姓名"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  電子郵件
                </label>
                <input
                  type="email"
                  value={userEmail}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
                  disabled
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">教材價格</span>
                <span className="font-semibold text-gray-900">HK$ {material?.price}</span>
              </div>
              <div className="border-t border-gray-200 pt-2 flex items-center justify-between">
                <span className="font-semibold text-gray-900">總計</span>
                <span className="text-2xl font-bold text-orange-500">HK$ {material?.price}</span>
              </div>
            </div>

            <button
              onClick={handlePurchase}
              disabled={purchasing}
              className="w-full py-4 bg-blue-900 text-white rounded-full text-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {purchasing ? '跳轉至支付頁面...' : '前往 Stripe 支付'}
            </button>

            <div className="flex items-center justify-center space-x-3 text-gray-400">
              <i className="ri-lock-line"></i>
              <span className="text-xs">安全支付由 Stripe 提供</span>
            </div>

            <p className="text-xs text-center text-gray-500">
              點擊確認購買即表示您同意我們的服務條款與隱私政策
            </p>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && user && isPurchased && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">評價教材</h2>
              <button
                onClick={() => setShowReviewModal(false)}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  您的評分
                </label>
                <div className="flex items-center justify-center space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      className="w-12 h-12 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                    >
                      <i
                        className={`text-4xl ${
                          star <= reviewRating
                            ? 'ri-star-fill text-yellow-500'
                            : 'ri-star-line text-gray-300'
                        }`}
                      ></i>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  評價內容（選填）
                </label>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="分享您對這份教材的使用心得..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                />
              </div>
            </div>

            <button
              onClick={handleSubmitReview}
              disabled={submittingReview}
              className="w-full py-4 bg-orange-500 text-white rounded-full text-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {submittingReview ? '提交中...' : '提交評價'}
            </button>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">回覆評價</h2>
              <button
                onClick={() => {
                  setShowReplyModal(false);
                  setReplyingToReviewId(null);
                  setAdminName('');
                  setAdminPassword('');
                  setReplyContent('');
                }}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  管理員名稱
                </label>
                <input
                  type="text"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  placeholder="請輸入您的名稱"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  管理員密碼
                </label>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="請輸入管理員密碼"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">預設密碼：admin123</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  回覆內容
                </label>
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="輸入您的回覆..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>

            <button
              onClick={handleSubmitReply}
              disabled={submittingReply}
              className="w-full py-4 bg-blue-900 text-white rounded-full text-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {submittingReply ? '提交中...' : '提交回覆'}
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © 2025 數學學習平台. 版權所有
            </div>
            <div>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
